"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDefaultTitleForSchema = exports.sortEntriesByKey = void 0;
var traverse_1 = __importDefault(require("@json-schema-tools/traverse"));
var crypto_1 = require("crypto");
var sortEntriesByKey = function (_a, _b) {
    var key1 = _a[0];
    var key2 = _b[0];
    return key1 > key2 ? -1 : 1;
};
exports.sortEntriesByKey = sortEntriesByKey;
var hashRegex = new RegExp("[^A-z | 0-9]+", "g");
/**
 * Returns the schema where if the schema.title is not set, a title will be generated based on the contents
 * of the schema. The properties of the schema are sorted such that the names are deterministic based on the
 * content of the schema. That being said, expect that any subtle change to the schema will result in a unique title.
 * If you've included titles on your schema, ensure that the titles are unique for their contents. If they are not,
 * ask yourself this: "why do two different schemas have the same name?".
 *
 * @param schema The JSON Schema to ensure has a title. If the schema is a composite schema (contains
 *                subschema(s)), the subschemas must already have titles.
 * @returns A new schema object that has all the fields of the one passed in, and a title if it didn't
 *          already have one.
 *
 */
function getDefaultTitleForSchema(schema, isRootCycle) {
    if (isRootCycle === void 0) { isRootCycle = false; }
    if (schema === true || schema === false) {
        return schema;
    }
    if (schema.title) {
        return schema;
    }
    if (schema.$ref) {
        throw new Error("There must not be any refs at this point. Ensure the passed in schema is completely dereferenced.");
    }
    var deterministicSchema = __assign({}, schema);
    var prefix = schema.type ? schema.type + "_" : "any_";
    ["anyOf", "oneOf", "allOf"].forEach(function (k) {
        if (schema[k]) {
            deterministicSchema[k] = schema[k].map(function (s) {
                if (isRootCycle && s === schema) {
                    return "self";
                }
                return s.title;
            }).sort();
            prefix = k + "_" + deterministicSchema[k].join("_") + "_";
        }
    });
    if (schema.type === "object" && schema.properties) {
        var sProps = schema.properties;
        deterministicSchema.properties = Object.entries(sProps).sort(exports.sortEntriesByKey);
        var joinedTitles = deterministicSchema.properties.map(function (val) {
            if (isRootCycle && val[1] === schema) {
                return "self";
            }
            return val[1].title;
        }).join("_");
        prefix = "objectOf_" + joinedTitles + "_";
    }
    if (schema.type === "array") {
        if (schema.items === true) {
            prefix = "setOfAny";
        }
        else if (schema.items === false) {
            prefix = "setOfNeverValid";
        }
        else if (schema.items instanceof Array === false) {
            var sItems = schema.items;
            deterministicSchema.items = Object.entries(sItems).sort(exports.sortEntriesByKey);
            var t = sItems.title;
            if (isRootCycle && sItems === schema) {
                t = "self";
            }
            prefix = "unorderedSetOf_" + t;
        }
        else {
            var joinedTitles = schema.items.map(function (itemSchema) {
                if (itemSchema === true) {
                    return "any";
                }
                if (itemSchema === false) {
                    return "never";
                }
                if (isRootCycle && itemSchema === schema) {
                    return "self";
                }
                return itemSchema.title;
            }).join("_");
            prefix = "unorderedSetOf_" + joinedTitles;
        }
    }
    if (schema.enum) {
        deterministicSchema.enum = schema.enum.slice(0).sort();
    }
    delete deterministicSchema.definitions;
    var asEntries = Object.entries(deterministicSchema).sort(exports.sortEntriesByKey);
    // circular refs can't be stringified
    var asString;
    try {
        asString = JSON.stringify(asEntries);
    }
    catch (e) {
        // as of recent updates, this should no longer ever be hit.
        // cycles have their titles resolved above.
        asString = JSON.stringify(asEntries, function (key, value) {
            if (value instanceof Array) {
                return key + "[]";
            }
            if (typeof value === "object") {
                return key + "{}";
            }
            return "" + key + value;
        });
    }
    var hash = crypto_1.createHash("sha1").update(asString).digest("base64");
    var friendlyHash = hash.replace(hashRegex, "").slice(0, 8);
    schema.title = "" + prefix + friendlyHash;
    return schema;
}
exports.getDefaultTitleForSchema = getDefaultTitleForSchema;
/**
 * Returns a copy of the schema (deep) where if any subschema does not have a title, one will be created.
 * The subschemas can be considered a tree of schemas, and in this case, we are resolving titles on the leaves
 * of the tree. Think depth first traversal, but where the internal nodes' titles are not resolved until
 * it's entire subtree is complete.
 *
 * @param s The schema to ensure has names for it and all subschemas of it.
 *
 * @returns Deep schema copy of the input schema where the schema and all sub schemas have titles.
 *
 * @category Utils
 * @category SchemaImprover
 *
 */
function titleizer(s) {
    traverse_1.default(s, getDefaultTitleForSchema, { mutable: true });
    return s;
}
exports.default = titleizer;
